import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button } from "../Buttons/Button";
import { useMediaQuery } from "react-responsive";
import { isSafeImageSrc, isSafeUrl } from "../../utils";

// CleverLink - Handles internal and external links
const CleverLink = ({ to, className = "", children }) => {
  const isExternal = to?.startsWith("http");
  const linkProps = {
    className,
    ...(isExternal
      ? {
        href: to,
        target: to.includes("patientsknowbest") ? "_self" : "_blank",
        rel: "noopener noreferrer",
      }
      : { to, target: "_self" }),
  };

  return isExternal ? (
    <a {...linkProps}>{children}</a>
  ) : (
    <Link {...linkProps}>{children}</Link>
  );
};

CleverLink.propTypes = {
  to: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

// Extract button rendering logic to reduce complexity
const renderButton = (to, button_text, button_append_class, isMobile) => {
  const isExternal = to?.startsWith("http");
  const commonProps = {
    className: `d-none1 d-md-block1 mt-2 ${button_append_class}`,
    label: button_text,
  };

  const style = isMobile
    ? { minWidth: "160px" }
    : { minWidth: "160px", fontWeight: "700" };

  return isExternal ? (
    <Button
      {...commonProps}
      color="primary"
      style={style}
    />
  ) : (
    <Button as={CleverLink} to={to} {...commonProps} />
  );
};

const renderButton2 = (button2_link, button2_text, button2_append_class) => {
  const shouldSkipClass =
    button2_text === "Return to my activation" &&
    button2_append_class === "disabled";
      
  return (
    <Button
      as={CleverLink}
      to={button2_link}
      variant="outline-primary"
      className={`mt-2 btn-outline-primary ${shouldSkipClass ? "" : button2_append_class
        }`}
      label={button2_text}
    />
  );
};

const renderButton3 = (button3_link, button3_text, button3_append_class) => (
  <Button
    as={CleverLink}
    to={button3_link}
    variant="outline-primary"
    className={`d-none1 d-md-block1 mt-2 btn-outline-primary ${button3_append_class}`}
    label={button3_text}
  />
);

const renderButton4 = (button4_link, button4_text, button4_target) => (
  <Button
    tag="a"
    href={button4_link}
    variant="outline-primary"
    className={`d-none1 d-md-block1 mt-2 ${button4_target}`}
    target={button4_target ? "_blank" : undefined}
    label={button4_text}
    onClick={(e) => e.stopPropagation()}
  />
);

// Button group component to eliminate duplication
const ButtonGroup = ({
  to,
  button_text,
  button_append_class,
  button2_link,
  button2_text,
  button2_append_class,
  button3_link,
  button3_text,
  button3_append_class,
  button4_link,
  button4_text,
  button4_target,
  isMobile,
}) => (
  <div className="d-flex flex-column gap-2 justify-content-center activate-btn-container result-list-buttons">
    {renderButton(to, button_text, button_append_class, isMobile)}
    {button2_link && renderButton2(button2_link, button2_text, button2_append_class)}
    {button3_link && renderButton3(button3_link, button3_text, button3_append_class)}
    {button4_link && isSafeUrl(button4_link) && renderButton4(button4_link, button4_text, button4_target)}
  </div>
);

ButtonGroup.propTypes = {
  to: PropTypes.string.isRequired,
  button_text: PropTypes.string,
  button_append_class: PropTypes.string,
  button2_link: PropTypes.string,
  button2_text: PropTypes.string,
  button2_append_class: PropTypes.string,
  button3_link: PropTypes.string,
  button3_text: PropTypes.string,
  button3_append_class: PropTypes.string,
  button4_link: PropTypes.string,
  button4_text: PropTypes.string,
  button4_target: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
};
// Mobile view component
const MobileView = ({ image, cardColor, title, titleColor, children, ...buttonProps }) => (
  <div
    data-testid="link-card card-color"
    className={`rounded d-flex flex-column flex-nowrap1 flex-wrap no-gutters padded-shadow-card shadow raise ${cardColor}`}
  >
    <div>
      <div className="d-flex flex-row pe-2 col-12 align-items-center test_header">
        {image !== null && isSafeImageSrc(image) && <img
          className="img-fluid can-mute"
          src={image}
          alt={title}
          width={100}
          height={100}
        />}
        <h5 className="mb-0">
          <strong>{title}</strong>
        </h5>
      </div>
      <div className="link-title w-100 mb-2">
        <div className="text-muted d-flex flex-column w-100">{children}</div>
      </div>
    </div>
    <ButtonGroup {...buttonProps} isMobile={true} />
  </div>
);

MobileView.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleColor: PropTypes.string,
  children: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
  button_text: PropTypes.string,
  button_append_class: PropTypes.string,
  button2_link: PropTypes.string,
  button2_text: PropTypes.string,
  button2_append_class: PropTypes.string,
  button3_link: PropTypes.string,
  button3_text: PropTypes.string,
  button3_append_class: PropTypes.string,
  button4_link: PropTypes.string,
  button4_text: PropTypes.string,
  button4_target: PropTypes.string,
  cardColor: PropTypes.string,
};

// Desktop view component
const DesktopView = ({ image, cardColor = "bg-white", title, titleColor, children, ts, ...buttonProps }) => (
  <div
    data-testid="link-card"
    className={`rounded d-flex flex-nowrap1 flex-wrap no-gutters mb-4 padded-shadow-card shadow raise ${cardColor}`}
  >{image !== null && isSafeImageSrc(image) &&
    <div className="d-flex pe-2 col-3 mw-100px min-width-100px">
      <img
        className="img-fluid can-mute p-2 mb-2"
        src={image}
        alt={title}
        width={100}
        height={100}
      />
    </div>}
    <div className="link-title me-2">
      {ts && <div className="text-muted mb-1">{ts}</div>}
      <h5 className="mb-2">
        <strong className={titleColor}>{title}</strong>
      </h5>
      <div className="text-muted">{children}</div>
    </div>
    <ButtonGroup {...buttonProps} isMobile={false} />
  </div>
);

DesktopView.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleColor: PropTypes.string,
  children: PropTypes.node.isRequired,
  ts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  to: PropTypes.string.isRequired,
  button_text: PropTypes.string,
  button_append_class: PropTypes.string,
  button2_link: PropTypes.string,
  button2_text: PropTypes.string,
  button2_append_class: PropTypes.string,
  button3_link: PropTypes.string,
  button3_text: PropTypes.string,
  button3_append_class: PropTypes.string,
  button4_link: PropTypes.string,
  button4_text: PropTypes.string,
  button4_target: PropTypes.string,
  cardColor: PropTypes.string,
};

// LinkCard Component
export const LinkCard = ({
  to,
  image,
  title,
  titleColor,
  button_text,
  button_append_class = "",
  children,
  button2_link,
  button2_text,
  button2_append_class = "",
  button3_link,
  button3_text,
  button3_append_class = "",
  button4_link,
  button4_text,
  button4_target,
  ts,
  postage_tracking,
  cardColor
}) => {
  const [isMdScreen, setIsMdScreen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 561 });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMdScreen(globalThis?.matchMedia("(max-width: 768px)")?.matches);
    };

    checkScreenSize();
    globalThis.addEventListener("resize", checkScreenSize);
    return () => globalThis.removeEventListener("resize", checkScreenSize);
  }, []);

  const ContainerTag = (button2_link || button4_link) && !isMdScreen ? "div" : CleverLink;

  const buttonProps = {
    to,
    button_text,
    button_append_class,
    button2_link,
    button2_text,
    button2_append_class,
    button3_link,
    button3_text,
    button3_append_class,
    button4_link,
    button4_text,
    button4_target,
  };

  const viewProps = { image, cardColor, title, titleColor, children, ts, ...buttonProps };

  return (
    <ContainerTag to={to} className="text-decoration-none text-reset">
      {isMobile ? (
        <MobileView {...viewProps} />
      ) : (
        <DesktopView {...viewProps} />
      )}
    </ContainerTag>
  );
};

LinkCard.propTypes = {
  button2_append_class: PropTypes.string,
  button2_link: PropTypes.string,
  button2_text: PropTypes.string,
  button3_append_class: PropTypes.string,
  button3_link: PropTypes.string,
  button3_text: PropTypes.string,
  button4_link: PropTypes.string,
  button4_target: PropTypes.string,
  button4_text: PropTypes.string,
  button_append_class: PropTypes.string,
  button_text: PropTypes.string,
  children: PropTypes.node.isRequired,
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  ts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  postage_tracking: PropTypes.any,
  titleColor: PropTypes.string,
  cardColor: PropTypes.string,
};